import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';

import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { useCustomerDataFilters } from 'contexts/CustomerDataFiltersContext';
import { useSmoothLoading } from 'lib/use-smooth-loading';
import { Input } from 'components/ui/input';
import { Combobox } from 'components/ui/combobox';

import {
    fetchKeyAccountsLanding,
    KeyAccountsLandingResponse,
    LandingSortKey,
} from './api';
import AccountCard from './components/AccountCard';
import PortfolioTotalsStrip from './components/PortfolioTotalsStrip';
import PaginationFooter from './components/PaginationFooter';

const SORT_OPTIONS: Array<{ value: string; label: string; by: LandingSortKey; dir: 'asc' | 'desc' }> = [
    { value: 'highest_exposure', label: 'Highest Exposure', by: 'total_exposure', dir: 'desc' },
    { value: 'highest_revenue', label: 'Highest Revenue (CY)', by: 'revenue_cy', dir: 'desc' },
    { value: 'lowest_growth', label: 'Lowest Revenue Growth', by: 'revenue_growth_pct', dir: 'asc' },
    { value: 'lowest_cm', label: 'Lowest CM %', by: 'cm_pct_cy', dir: 'asc' },
    { value: 'highest_overdue', label: 'Highest Overdue >30', by: 'overdue_gt_30', dir: 'desc' },
    { value: 'highest_dso', label: 'Highest DSO', by: 'dso', dir: 'desc' },
    { value: 'highest_unbilled', label: 'Highest Unbilled', by: 'unbilled_excl_stock', dir: 'desc' },
    { value: 'name', label: 'Customer Name (A–Z)', by: 'customer_name', dir: 'asc' },
];

const KeyAccountsLandingPage: React.FC = () => {
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();
    const { years, year, handleYearChange, error: filtersError } = useCustomerDataFilters();

    const lastQueryRef = React.useRef<string | null>(null);

    const [data, setData] = React.useState<KeyAccountsLandingResponse | null>(null);
    const [dataLoading, setDataLoading] = React.useState<boolean>(false);
    const [dataError, setDataError] = React.useState<string>('');
    const [hasFetchedOnce, setHasFetchedOnce] = React.useState<boolean>(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = React.useState<string>(searchParams.get('search') || '');
    const [debouncedSearch, setDebouncedSearch] = React.useState<string>(searchParams.get('search') || '');
    const [sortValue, setSortValue] = React.useState<string>('highest_exposure');
    const [page, setPage] = React.useState<number>(1);
    const [pageSize, setPageSize] = React.useState<number>(12);

    const { isLoading: smoothLoading, showContent } = useSmoothLoading(dataLoading, {
        minLoadingTime: 200,
        debounceTime: 200,
    });

    React.useEffect(() => {
        setBreadcrumbs([
            { label: 'Reports', href: '/reports/pnl' },
            { label: 'Key Accounts' },
        ]);
    }, [setBreadcrumbs]);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    React.useEffect(() => {
        if (!year) return;

        const sort = SORT_OPTIONS.find((o) => o.value === sortValue) || SORT_OPTIONS[0];
        const queryKey = JSON.stringify({ year, search: debouncedSearch, sortValue, page, pageSize });
        if (lastQueryRef.current === queryKey) return;
        lastQueryRef.current = queryKey;

        setDataLoading(true);
        setDataError('');

        fetchKeyAccountsLanding({
            year,
            ...(debouncedSearch && { search: debouncedSearch }),
            sort: { by: sort.by, dir: sort.dir },
            page,
            page_size: pageSize,
        })
            .then(setData)
            .catch((err: unknown) => {
                const isForbidden = (err as any)?.status === 403;
                setDataError(
                    isForbidden
                        ? 'You are not allowed to access this resource.'
                        : err instanceof Error
                            ? err.message
                            : 'Failed to fetch key accounts'
                );
                setData(null);
            })
            .finally(() => {
                setDataLoading(false);
                setHasFetchedOnce(true);
            });
    }, [year, debouncedSearch, sortValue, page, pageSize]);

    const handleCardClick = React.useCallback(
        (customerCode: string, customerName: string) => {
            const params = new URLSearchParams({ customerName: customerName.trim() });
            navigate(`/reports/key-accounts/${encodeURIComponent(customerCode)}?${params.toString()}`);
        },
        [navigate]
    );

    const items = data?.items ?? [];

    return (
        <div className="space-y-5 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6">

            {/* Filters */}
            <div className="flex flex-col lg:flex-row gap-4 lg:items-end">
                <div className="flex gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Financial Year</label>
                        <Combobox
                            className="w-44"
                            options={years.map((y) => ({ value: y, label: y }))}
                            value={year}
                            onValueChange={(y) => {
                                setPage(1);
                                handleYearChange(y);
                            }}
                            placeholder="Select year..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Division</label>
                        <Input
                            className="w-52 bg-gray-50 text-gray-600"
                            value={data?.division_name || ''}
                            disabled
                            readOnly
                        />
                    </div>
                </div>

                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        placeholder="Search key account..."
                        value={searchTerm}
                        onChange={(e) => {
                            const val = e.target.value;
                            setSearchTerm(val);
                            const next = new URLSearchParams(searchParams);
                            if (val.trim()) next.set('search', val.trim());
                            else next.delete('search');
                            setSearchParams(next, { replace: true });
                        }}
                        className="pl-10"
                    />
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 whitespace-nowrap">Sort by:</span>
                    <Combobox
                        className="w-56"
                        options={SORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                        value={sortValue}
                        onValueChange={(v) => {
                            if (!v) return;
                            setPage(1);
                            setSortValue(v);
                        }}
                    />
                </div>
            </div>

            {/* Errors */}
            {(dataError || filtersError) && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{dataError || filtersError}</p>
                </div>
            )}

            {/* Portfolio totals */}
            {!dataError && data && data.total > 0 && (
                <PortfolioTotalsStrip portfolio={data.portfolio} />
            )}

            {/* Loading */}
            {smoothLoading && (
                <div className="grid grid-cols-1 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="bg-white rounded-lg border shadow-sm p-5 space-y-4">
                            <div className="h-5 bg-gray-200 animate-pulse rounded w-2/3" />
                            <div className="h-3 bg-gray-200 animate-pulse rounded w-1/3" />
                            <div className="grid grid-cols-6 gap-3 pt-2">
                                {Array.from({ length: 6 }).map((_, j) => (
                                    <div key={j} className="space-y-2">
                                        <div className="h-3 bg-gray-200 animate-pulse rounded" />
                                        <div className="h-5 bg-gray-200 animate-pulse rounded" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty */}
            {!smoothLoading && hasFetchedOnce && !dataError && items.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                    {debouncedSearch
                        ? `No key accounts match "${debouncedSearch}"`
                        : 'No Key Accounts Assigned Yet'}
                </div>
            )}

            {/* Cards */}
            {!smoothLoading && showContent && data && items.length > 0 && (
                <div className="space-y-4">
                    <div className="text-sm text-gray-600">
                        Showing {items.length} of {data.total} key accounts
                        {debouncedSearch && ` for "${debouncedSearch}"`}
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        {items.map((account) => (
                            <AccountCard
                                key={account.customer_code}
                                account={account}
                                onClick={handleCardClick}
                            />
                        ))}
                    </div>

                    <PaginationFooter
                        page={page}
                        pageSize={pageSize}
                        totalItems={data.total}
                        onPageChange={setPage}
                        onPageSizeChange={(s) => {
                            setPage(1);
                            setPageSize(s);
                        }}
                        itemLabel="key accounts"
                    />
                </div>
            )}
        </div>
    );
};

export default KeyAccountsLandingPage;
